import { type CSSProperties, type PropsWithChildren, useEffect, useState } from 'react';

import type { JSONContent } from '@tiptap/core';

import EmblaCarousel from '@/components/EmblaCarousel';
import { ContainerElement } from '@/containerElement';
import { Heading3Element } from '@/headingElement';
import { ImageElement } from '@/imageElement';
import { ParagraphElement } from '@/paragraphElement';
import SearchingState from '@/postElement/pagination/SeachingState';
import SearchInput from '@/postElement/pagination/SearchInput';
import type { SerializableNode } from '@/serializer';
import { SerializeTextLeaf } from '@/textLeaf';
import type { RSSFeedDataPodcast, RSSFeedDataPodcastItem } from '@/types';
import { EmptyState } from '@/ui/EmptyState/EmptyState';
import { cn } from '@/utils/cn';
import { useMobileCarouselSettings } from '@/utils/useMobileCarouselSettings';

import { RSS_FEED_DEFAULTS } from './constants';
import * as styles from './RSSFeed.css';
import type { RSSFeedElementProps } from './RSSFeed.types.js';
import { PodcastSerializer } from './Serializer';
import { formatDate, getEpisodeUrl, normalizeToArray, stripHtmlTags } from './util.js';

const COLUMNS_MAP: Record<string, string> = {
  '1': 'grid-cols-1',
  '2': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2',
  '3': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  '4': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  '5': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5',
  '6': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-6',
};

const OPTIONS: any = {
  align: 'start',
  containScroll: 'trimSnaps',
};
export const PodcastRssFeed = (props: PropsWithChildren<RSSFeedElementProps>) => {
  const { element, attributes, children, className, isPreviewing, rssFeedMap = {}, isLive = true, viewport } = props;
  const {
    url,
    data: rawData,
    gap,
    columns,
    padding,
    itemPadding,
    itemGap,
    textGap,
    imageBorderRadius,
    titleFontSize,
    titleFontFamily,
    titleTextColor,
    titleFontWeight,
    titleAlignment,
    titleLineClamp,
    subtitleFontSize,
    subtitleFontFamily,
    subtitleTextColor,
    subtitleFontWeight,
    subtitleAlignment,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    backgroundColor,
    imageAspectRatio,
    imageFit,
    hideDate,
    isScrollHorizontally,
    isMobileScrollHorizontally,
    rssSelectionType,
    selectedRSSItems,
    rssOffset,

    // Search Input
    inputBackgroundColor,
    inputTextColor,
    inputPlaceholderColor,
    inputBorderRadius,
    inputBorderWidth,
    inputBorderColor,
    inputBorderStyle,
    inputEnabled,
    inputBorderFocus,
    inputIconFill,
    inputAlignment,
    inputMaxWidth,
    inputMaxHeight,
    inputMarginBottom,

    // Empty State
    emptyStatePadding,
    emptyStateBackgroundColor,
    emptyStateTextColor,
    emptyStateText,
    emptyStateFontSize,

    // Card structure
    structureTiptapState,
    hasMigratedToCardStructure,
  } = element.attrs;

  const isViewportMobile = viewport === 'mobile';

  const {
    columns: effectiveColumns,
    shouldScrollHorizontally: displayedShouldScrollHorizontally,
    sliderControlsDisplayType: displayedSliderControlsDisplayType,
    sliderControlsBackgroundColor: displayedSliderControlsBackgroundColor,
    sliderControlsTextColor: displayedSliderControlsTextColor,
    sliderControlsBorderColor: displayedSliderControlsBorderColor,
  } = useMobileCarouselSettings({
    attributes: {
      ...element.attrs,
      shouldScrollHorizontally: isScrollHorizontally,
      mobileShouldScrollHorizontally: isMobileScrollHorizontally,
    },
    isViewportMobile: isViewportMobile || false,
  });

  const showCount = element.attrs.showCount || 5;

  const data = rawData as RSSFeedDataPodcast;

  const feedItems = normalizeToArray(data?.rss?.channel?.item);

  // If using free selection with selected items, skip data validation
  const hasSelectedItems = rssSelectionType === 'free_selection' && selectedRSSItems && selectedRSSItems.length > 0;

  const desktopSlideSize =
    displayedShouldScrollHorizontally && effectiveColumns ? `${100 / Number.parseInt(effectiveColumns)}%` : '';

  const offset = rssOffset || 0;

  // Use selected items for free selection, otherwise use latest from feed with offset
  const defaultShownData = hasSelectedItems
    ? (selectedRSSItems as RSSFeedDataPodcastItem[])
    : feedItems.slice(offset, offset + showCount);
  const [shownData, setShownData] = useState(defaultShownData);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    setShownData(defaultShownData);
  }, [hasSelectedItems, offset, showCount, selectedRSSItems, feedItems.length]);

  const onSearchQueryChange = (query: string) => {
    if (!query) {
      setShownData(defaultShownData);
      return;
    }
    const lowerQuery = query.toLowerCase();
    if (hasSelectedItems) {
      const items = selectedRSSItems as RSSFeedDataPodcastItem[];
      const filtered = items.filter((item) => {
        const plainDescription = stripHtmlTags(item.description || '').toLowerCase();
        return item.title?.toLowerCase().includes(lowerQuery) || plainDescription.includes(lowerQuery);
      });
      setShownData(filtered);
      return;
    }

    const filtered = feedItems
      .filter((item) => {
        const plainDescription = stripHtmlTags(item.description || '').toLowerCase();
        return item.title?.toLowerCase().includes(lowerQuery) || plainDescription.includes(lowerQuery);
      })
      .slice(offset, offset + showCount);
    setShownData(filtered);
  };

  if (!hasSelectedItems && !data?.rss?.channel) {
    return (
      <div className={cn('grid relative w-full h-fit', className, COLUMNS_MAP[columns || '3'])} {...attributes}>
        <p className="text-sm text-gray-900 text-left leading-tight">Unsupported RSS feed source</p>
        <p className="text-xs text-gray-500 text-left leading-tight">{url}</p>
      </div>
    );
  }

  const cardNode = structureTiptapState?.content?.[0] as JSONContent & SerializableNode;

  const evaluateEpisodeUrl = (entryTitle: string, url: string) => {
    const rssFeedItem = rssFeedMap?.[url];
    const rssFeedUUID = rssFeedItem?.uuid;
    return getEpisodeUrl(entryTitle, rssFeedUUID);
  };

  return (
    <div
      className="relative flex flex-col w-full"
      style={
        {
          // Search Input
          '--input-background-color': inputBackgroundColor,
          '--input-text-color': inputTextColor,
          '--input-placeholder-color': inputPlaceholderColor,
          '--input-border-radius': inputBorderRadius,
          '--input-border-width': inputBorderWidth,
          '--input-border-color': inputBorderColor,
          '--input-border-style': inputBorderStyle,
          '--input-border-focus': inputBorderFocus,
          '--input-icon-fill': inputIconFill,
          '--input-max-width': inputMaxWidth,
          '--input-max-height': inputMaxHeight,
          '--input-margin-bottom': inputMarginBottom,
        } as CSSProperties
      }
      {...attributes}
    >
      <SearchInput
        alignment={inputAlignment}
        onChange={onSearchQueryChange}
        isVisible={!!inputEnabled}
        setIsSearching={setIsSearching}
        handleNonDebouncedSearchQuery={() => {}}
        handleResetSearchState={() => setShownData(defaultShownData)}
      />
      <div
        className={cn(
          'relative w-full h-fit items-stretch',
          className,
          styles.rssFeedGrid,
          displayedShouldScrollHorizontally ? 'flex' : 'grid',
          !displayedShouldScrollHorizontally && COLUMNS_MAP[effectiveColumns || '3'],
        )}
        style={
          {
            '--card-border-radius': borderRadius || RSS_FEED_DEFAULTS.borderRadius,
            '--card-border-width': borderWidth || RSS_FEED_DEFAULTS.borderWidth,
            '--card-border-color': borderColor || RSS_FEED_DEFAULTS.borderColor,
            '--card-border-style': borderStyle || RSS_FEED_DEFAULTS.borderStyle,
            '--gap': gap || '16px',
            padding: padding,
            // Desktop Slider
            '--desktop-slide-size': desktopSlideSize,
            '--post-slider-control-background-color': displayedSliderControlsBackgroundColor,
            '--post-slider-control-text-color': displayedSliderControlsTextColor,
            '--post-slider-control-border-color': displayedSliderControlsBorderColor,

            // Mobile Slider
            '--mobile-slider-control-background-color':
              element.attrs.mobileSliderControlsBackgroundColor || displayedSliderControlsBackgroundColor,
            '--mobile-slider-control-text-color':
              element.attrs.mobileSliderControlsTextColor || displayedSliderControlsTextColor,
            '--mobile-slider-control-border-color':
              element.attrs.mobileSliderControlsBorderColor || displayedSliderControlsBorderColor,
          } as CSSProperties
        }
      >
        <EmblaCarousel
          options={OPTIONS}
          isCarousel={!!displayedShouldScrollHorizontally}
          isLive={isLive}
          controlsDisplayType={displayedSliderControlsDisplayType}
        >
          {hasMigratedToCardStructure &&
            structureTiptapState &&
            shownData.map((entry) => (
              <div
                key={entry.guid}
                className={cn('embla__slide relative podcast-serializer-container', isLive && 'cursor-pointer')}
              >
                {isLive ? (
                  <a
                    href={evaluateEpisodeUrl(entry.title, url)}
                    target="_blank"
                    className="relative z-10 h-full embla__slide__number"
                    rel="noopener noreferrer"
                  >
                    <PodcastSerializer
                      key={entry.guid}
                      node={cardNode}
                      rootClassName="h-full"
                      rootStyle={{}}
                      episodeTitle={entry.title}
                      episodeDescription={stripHtmlTags(entry.description || '')}
                      episodeUrl={evaluateEpisodeUrl(entry.title, url)}
                      episodePublished={!hideDate ? formatDate(entry.pubDate) : ''}
                      episodeThumbnailUrl={
                        entry['itunes:image']?.['@href'] ||
                        data?.rss?.channel?.image?.url ||
                        data?.rss?.channel?.['itunes:image']?.['@href']
                      }
                      isLive={isLive}
                    >
                      {children}
                    </PodcastSerializer>
                  </a>
                ) : (
                  <PodcastSerializer
                    key={entry.guid}
                    node={cardNode}
                    rootClassName="h-full"
                    rootStyle={{}}
                    episodeTitle={entry.title}
                    episodeDescription={stripHtmlTags(entry.description || '')}
                    episodeUrl={evaluateEpisodeUrl(entry.title, url)}
                    episodePublished={!hideDate ? formatDate(entry.pubDate) : ''}
                    episodeThumbnailUrl={
                      entry['itunes:image']?.['@href'] ||
                      data?.rss?.channel?.image?.url ||
                      data?.rss?.channel?.['itunes:image']?.['@href']
                    }
                    isLive={isLive}
                  >
                    {children}
                  </PodcastSerializer>
                )}
              </div>
            ))}
          {!hasMigratedToCardStructure &&
            shownData.map((entry) => (
              <ContainerElement
                href={entry.link}
                key={entry.guid}
                className={cn('flex flex-col items-start cursor-pointer embla__slide relative', styles.youtubeCard)}
                target="_blank"
                rel="noopener noreferrer"
                element={{
                  type: 'container',
                  attrs: {
                    id: 'podcast-container',
                    padding: itemPadding,
                    position: 'relative',
                    verticalAlign: 'align_start',
                    backgroundColor: backgroundColor,
                    height: 'auto',
                    borderRadius: borderRadius,
                    overflow: 'hidden',
                    borderWidth: borderWidth,
                    borderColor: borderColor,
                    borderStyle: borderStyle,
                    gap: itemGap || '8px',
                  },
                  content: [],
                }}
                onClick={() => {
                  if (!isPreviewing) {
                    const episodeURL = evaluateEpisodeUrl(entry.title, url);
                    window.open(episodeURL, '_blank');
                  }
                }}
              >
                <ImageElement
                  element={{
                    type: 'image',
                    attrs: {
                      src:
                        entry['itunes:image']?.['@href'] ||
                        data?.rss?.channel?.image?.url ||
                        data?.rss?.channel?.['itunes:image']?.['@href'],
                      width: '100%',
                      height: 'auto',
                      aspectRatio: imageAspectRatio || '16/9',
                      objectFit: imageFit || 'cover',
                      borderRadius: imageBorderRadius || RSS_FEED_DEFAULTS.imageBorderRadius,
                    },
                  }}
                />
                <ContainerElement
                  element={{
                    type: 'container',
                    attrs: {
                      id: 'container',
                      backgroundColor: 'transparent',
                      padding: '0px',
                      gap: textGap || '8px',
                      borderWidth: 0,
                    },
                    content: [],
                  }}
                >
                  <Heading3Element
                    element={{
                      type: 'heading',
                      attrs: {
                        id: 'heading3',
                        anchorId: 'heading3',
                        padding: '0px',
                        anchorTitle: 'heading3',
                        anchorIdSync: true,
                        anchorEnabled: true,
                        anchorTitleSync: true,
                        customAttributes: {},
                        textAlign: titleAlignment || 'left',
                        level: 3,
                      },
                      content: [
                        {
                          text: entry.title,
                        },
                      ],
                    }}
                    className={titleLineClamp ? `${titleLineClamp} text-ellipsis` : ''}
                  >
                    <SerializeTextLeaf
                      leaf={{
                        text: entry.title,
                        marks: [
                          {
                            type: 'textStyle',
                            attrs: {
                              fontSize: titleFontSize || RSS_FEED_DEFAULTS.titleFontSize,
                              fontFamily: titleFontFamily || RSS_FEED_DEFAULTS.titleFontFamily,
                              color: titleTextColor || RSS_FEED_DEFAULTS.titleTextColor,
                              fontWeight: titleFontWeight || RSS_FEED_DEFAULTS.titleFontWeight,
                            },
                          },
                        ],
                      }}
                      className={titleLineClamp ? `${titleLineClamp} text-ellipsis` : ''}
                    />
                  </Heading3Element>
                  {!hideDate && (
                    <ParagraphElement
                      element={{
                        type: 'paragraph',
                        attrs: {
                          textAlign: subtitleAlignment || 'left',
                          padding: '0px',
                        },
                      }}
                    >
                      <SerializeTextLeaf
                        leaf={{
                          text: formatDate(entry.pubDate),
                          marks: [
                            {
                              type: 'textStyle',
                              attrs: {
                                fontSize: subtitleFontSize || RSS_FEED_DEFAULTS.subtitleFontSize,
                                fontFamily: subtitleFontFamily || RSS_FEED_DEFAULTS.subtitleFontFamily,
                                color: subtitleTextColor || RSS_FEED_DEFAULTS.subtitleTextColor,
                                fontWeight: subtitleFontWeight || RSS_FEED_DEFAULTS.subtitleFontWeight,
                              },
                            },
                          ],
                        }}
                      />
                    </ParagraphElement>
                  )}
                </ContainerElement>
              </ContainerElement>
            ))}
        </EmblaCarousel>
        {children}
      </div>

      {isSearching && (
        <SearchingState
          style={
            {
              gap: gap,
            } as CSSProperties
          }
          className={cn(
            styles.rssFeedGrid,
            displayedShouldScrollHorizontally ? 'flex' : 'grid',
            !displayedShouldScrollHorizontally && COLUMNS_MAP[effectiveColumns || '3'],
          )}
        />
      )}

      {!isSearching && (!shownData || shownData.length === 0) ? (
        <EmptyState
          padding={emptyStatePadding}
          backgroundColor={emptyStateBackgroundColor}
          textColor={emptyStateTextColor}
          fontSize={emptyStateFontSize}
        >
          {emptyStateText}
        </EmptyState>
      ) : null}
    </div>
  );
};
